import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Breadcrumbs,
  Button,
  Box,
  Typography,
} from '@mui/material';

import { useAppDispatch } from '../../hooks/reduxHooks';
import ContentBox from '../../components/layout/ContentBox';
import {
  fetchFullDigitalSurvey,
  setSelectedCase,
  setSelectedDigitalsection,
  showErrorNotification,
  fetchDigitalSubmissionMeta,
  setSelectedDigitalSubmissionMeta,
  fetchTrial,
} from '../../redux/actions';
import {
  selectDigitalSections,
  selectSelectedSection,
  selectSelectedSubmissionMeta,
} from '../../redux/reducers/SurveyDataReducer';
import { DigitalSection, RootState } from '../../redux/types';

import { createGrid } from '../../utilities/arrayHelper';
import { getRemainingTimeCounter } from '../../components/digitalTrial/DigitalTimer';
import Prompt from '../../components/layout/Prompt';
import ApiClient from '../../loopback/apiClient';
import { checkTrialHasExpired } from '../../components/utilities/TrialUtility';
import ColorContext from '../../context/ColorContext';

type MatchParams = {
  trialId: string;
  surveyId: string;
};
const AntibodySelectionView: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const params = useParams<MatchParams>();
  const { trialId, surveyId } = params;

  const trial = useSelector((store: RootState) => store.trial.trial);
  const [showPrompt, setShowPrompt] = useState<boolean>(false);
  const [trialHasExpired, setTrialHasExpired] = useState<boolean>(false);

  /** Redux Selectors */
  const antiBodys = useSelector((state: RootState) => selectDigitalSections(state));
  const survey = useSelector((state: RootState) => state.SurveyData.survey);
  const digitalSubmissionMetaMap = useSelector(
    (state: RootState) => state.SurveyData.digitalSubmissionMeta.byId,
  );
  const digitalSubmissionMeta = useSelector(
    (state: RootState) => selectSelectedSubmissionMeta(state),
  );
  const selectedSection = useSelector((state: RootState) => selectSelectedSection(state));
  const currentDigitalSubmissionMetaIds = useSelector(
    (state: RootState) => state.SurveyData.digitalSubmissionMeta.allIds,
  );

  useEffect(() => {
    dispatch(fetchFullDigitalSurvey(Number(surveyId)));
    dispatch(fetchDigitalSubmissionMeta(Number(trialId)));
    dispatch(fetchTrial(Number(trialId)));
  }, [dispatch, surveyId, trialId]);

  useEffect(() => {
    // empty old cached selected section and case on component mount
    dispatch(setSelectedDigitalsection(undefined));
    dispatch(setSelectedCase());
  }, [dispatch]);

  useEffect(() => {
    setTrialHasExpired(checkTrialHasExpired(trial));
  }, [trial]);

  const getSubmissionMetaFromSectionId = useCallback(
    (sectionId: number) => Object.values(digitalSubmissionMetaMap)
      .find((dsm) => dsm.selectedSectionId === sectionId), [
      digitalSubmissionMetaMap,
    ],
  );

  const onAntibodyClick = (ds: DigitalSection) => {
    dispatch(setSelectedDigitalsection(ds.id));
    const dsm = getSubmissionMetaFromSectionId(ds.id);
    dispatch(setSelectedDigitalSubmissionMeta(dsm?.id));
    if (survey?.exercise
      || getSubmissionMetaFromSectionId(ds.id)?.submissionFinalized
      || trialHasExpired) {
      navigate(`/digitaltrial/${trialId}/survey/${surveyId}/antibody/${ds.id}/form`);
    } else {
      setShowPrompt(true);
    }
  };

  const handlePrompt = async (confirm: boolean) => {
    if (!confirm) {
      setShowPrompt(false);
      dispatch(setSelectedDigitalsection(undefined));
      dispatch(setSelectedDigitalSubmissionMeta(undefined));
      return;
    }
    let dsm = digitalSubmissionMeta;
    if (!dsm && selectedSection) {
      // digital submission meta is created here first time when user starts the exam
      dsm = await ApiClient.initializeSubmissionMeta(
        Number(trialId), selectedSection.id,
      );
    }
    if (dsm && selectedSection && dsm.selectedSectionId === selectedSection.id) {
      navigate(`/digitaltrial/${trialId}/survey/${surveyId}/antibody/${selectedSection.id}/form`);
    } else {
      dispatch(showErrorNotification('Initialized section and selected section mismatch.'));
      setShowPrompt(false);
    }
  };

  const getRemainingAntibodyTime = (sectionId: number) => {
    const dsm = getSubmissionMetaFromSectionId(sectionId);
    const remainingAntibodyTime = dsm
      && dsm.checkpoint
      && dsm.endTime
      ? getRemainingTimeCounter(
        new Date(dsm.checkpoint).getTime(),
        new Date(dsm.endTime).getTime(),
      ).map((v) => String(v).padStart(2, '0')).join(':')
      : null;
    return remainingAntibodyTime;
  };

  const renderAntibodySelection = (ds: DigitalSection) => {
    const existingSubmissionMeta = getSubmissionMetaFromSectionId(ds.id);
    let disableButton = false;
    if (trialHasExpired && currentDigitalSubmissionMetaIds.length === 0) {
      disableButton = true;
    }

    return (
      <Button
        color="tertiary"
        disabled={
          disableButton || (
            !survey?.exercise
            && !survey?.allowMultipleSectionSubmission
            && !existingSubmissionMeta
            && currentDigitalSubmissionMetaIds.length > 0
          )
        }
        onClick={() => { onAntibodyClick(ds); }}
        sx={{ width: 210, minHeight: 90 }}
        variant="contained"
        key={ds.id}
        size="large"
      >
        {ds.name}
        {'\r\n'}
        {
          getRemainingAntibodyTime(ds.id)
        }
      </Button>
    );
  };
  return (
    <ColorContext.Provider value="tertiary">
      <ContentBox title={`Digital Readout Test ${survey?.exercise ? 'Exercise' : ''}`} elevation={0} color="tertiary">
        <Box sx={{ mb: 2 }}>
          <Breadcrumbs aria-label="breadcrumb">
            <Link color="textSecondary" to={`/digitalTrial/${trialId}/menu`}>Menu</Link>
            <Link color="textPrimary" to={`/digitaltrial/${trialId}/survey/${surveyId}/antibodies`}>
              {survey?.exercise ? 'Exercises' : 'Antibodies'}
            </Link>
          </Breadcrumbs>
        </Box>
        <div>
          {(trialHasExpired && !survey?.exercise)
            ? (<Typography variant="h4">Trial has expired. No new selection can be made.</Typography>)
            : (<Typography variant="h4">Please select an antibody to perform the test.</Typography>)}
        </div>
        <Container className="mt4" maxWidth="md">
          {
            createGrid<DigitalSection>(Object.values(antiBodys), 3).map((rowEl, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <Box sx={{ my: '25px' }} display="flex" justifyContent="space-evenly" key={i}>
                {
                  rowEl.map((ds) => renderAntibodySelection(ds))
                }
              </Box>
            ))
          }
        </Container>
      </ContentBox>
      <Prompt
        title={digitalSubmissionMeta ? 'Continue the Exam ' : 'Start the Exam'}
        display={!!showPrompt && !!selectedSection}
        action={handlePrompt}
        color="tertiary"
        language="en"
        version={2}
      >
        {
          digitalSubmissionMeta && selectedSection
            ? `Would you like to continue with your Digital Readout Test? ${survey?.time ? `There are ${getRemainingAntibodyTime(selectedSection?.id)} remaining for the test to end.` : ''}`
            : `${survey?.allowMultipleSectionSubmission ? `Would you like to start the Antibody ${selectedSection?.name}` : 'Once a selection is being made, you can no longer change the selected antibody'}. ${survey?.time ? `You have a total time of ${survey?.time} minutes to perform the test, however you are free to pause at any time.` : ''}`
        }
      </Prompt>
    </ColorContext.Provider>
  );
};

export default AntibodySelectionView;
